/**
 * The name a role is shown under.
 *
 * Users carry their role as its key (`roles[0]`, e.g. `csr`, `msp_owner`), not its label. The label
 * lives on the role itself (`displayName`, see `useRoleCatalogue`), so every place that shows a role
 * goes through `formatRoleName` with the same catalogue — the Employees list and the staffer header
 * then always agree on what a role is called.
 */

/**
 * Labels for the roles that predate `displayName`. Only a fallback: used when no catalogue was
 * asked for, when the lookup failed, or when the catalogue does not know the key.
 */
const transformRoleName = (role: string): string => {
  switch (role.toLowerCase()) {
    case 'companyadmin':
      return 'Company Admin'
    case 'agencyadmin':
      return 'Agency Admin'
    case 'mainagencyadmin':
      return 'Main Agency Admin'
    case 'generaladmin':
    case 'general_admin':
      return 'General Admin'
    case 'platformadmin':
      return 'Platform Admin'
    case 'mspadmin':
      return 'MSP Admin'
    case 'recruiter':
      return 'Recruiter'
    case 'businessanalyst':
      return 'Business Analyst'
    default:
      return role
  }
}

/**
 * A role key's display name: the catalogue's `displayName` (the catalogue is keyed by lower-cased
 * role name), then the legacy labels above, then the key itself — so a custom role the catalogue
 * cannot see (`onsite`) still shows, just unformatted.
 */
export function formatRoleName(key: string, catalogue?: ReadonlyMap<string, string>): string {
  return catalogue?.get(key.toLowerCase()) ?? transformRoleName(key)
}
